import { useEffect, useMemo, useState } from 'react';
import { Eye, EyeSlash } from '@phosphor-icons/react';
import { useAuth } from '@/contexts/AuthContext';
import API, { fmt } from '@/lib/api';
import { scanDocumentLine } from '@/lib/scanQuantity';

export const ScanProfit = ({ items, invalid, saving = false }) => {
  const { user } = useAuth();
  const allowed = user?.role === 'admin' || !!user?.mag_winst_zien;
  const identity = user ? `${user.id || user._id || user.email}:${user.role}:${user.verkoper_id || ''}` : '';
  const [shownFor, setShownFor] = useState(null);
  const [snapshot, setSnapshot] = useState(null);
  const [retry, setRetry] = useState(0);
  const visible = allowed && shownFor === identity && !saving;
  const payload = useMemo(() => JSON.stringify({ lijnen: items.map(i => scanDocumentLine(i)) }), [items]);
  useEffect(() => { if (!allowed || !items.length || saving) setShownFor(null); }, [allowed, items.length, saving]);

  useEffect(() => {
    if (!visible || invalid || !items.length) { setSnapshot(null); return; }
    const controller = new AbortController();
    let current = true;
    const timer = setTimeout(async () => {
      try {
        const r = await API.post('/snel-scannen/winst', JSON.parse(payload), { signal: controller.signal, timeout: 15000 });
        if (current) setSnapshot({ identity, payload, data: r.data });
      } catch (e) {
        if (current && e.code !== 'ERR_CANCELED') setSnapshot({ identity, payload,
          error: e.response?.status === 403 ? 'Geen toegang tot winstgegevens.' : 'Winst kon niet worden berekend.' });
      }
    }, 250);
    return () => { current = false; clearTimeout(timer); controller.abort(); };
  }, [visible, invalid, items.length, identity, payload, retry]);

  if (!allowed) return null;
  const result = snapshot?.identity === identity && snapshot?.payload === payload ? snapshot : null;
  const toggle = () => { setSnapshot(null); setShownFor(visible ? null : identity); };
  return <div className="mt-2 text-sm" data-testid="scan-profit-section">
    <div className="flex items-center flex-wrap gap-x-3 gap-y-1 min-h-8">
      <span className="font-bold text-black uppercase" data-testid="scan-profit-label">Winst</span>
      {!visible ? <span className="text-gray-400 tracking-widest" data-testid="scan-profit-hidden" aria-label="Winst verborgen">••••</span>
        : invalid ? <span className="text-xs text-gray-500" data-testid="scan-profit-invalid">Controleer de invoer</span>
        : !items.length ? <span className="text-xs text-gray-500" data-testid="scan-profit-empty">Geen regels</span>
        : !result ? <span role="status" className="text-xs text-gray-500" data-testid="scan-profit-loading">Berekenen…</span>
        : result.error ? <span role="alert" className="text-xs text-red-700" data-testid="scan-profit-error">{result.error}</span>
        : result.data.winst == null ? <span className="text-xs text-amber-700" data-testid="scan-profit-missing">Inkoopprijs ontbreekt bij {result.data.ontbrekende_inkoopprijzen} {result.data.ontbrekende_inkoopprijzen === 1 ? 'regel' : 'regels'}</span>
        : <strong className={result.data.winst < 0 ? 'text-red-700' : 'text-green-700'} data-testid="scan-profit-value">{fmt(result.data.winst)}</strong>}
      <button type="button" onClick={toggle} disabled={!items.length} aria-expanded={visible}
        className="inline-flex items-center gap-1.5 py-1 text-xs text-gray-600 hover:text-black disabled:opacity-40 transition-colors"
        data-testid="scan-profit-toggle" title={visible ? 'Winst verbergen' : 'Winst tonen'}>
        {visible ? <EyeSlash size={16} /> : <Eye size={16} />}{visible ? 'Verbergen' : 'Tonen'}
      </button>
      {visible && result?.error && <button type="button" className="text-xs underline" data-testid="scan-profit-retry"
        onClick={() => { setSnapshot(null); setRetry(v => v + 1); }}>Opnieuw</button>}
    </div>
  </div>;
};